import {
  NotificationService,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiRecordDatasource as OuiDatasource,
  OuiFileDropZoneComponent,
  OuiRowComponent,
  OuiStackComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CardComponent } from '../../components/card/card.component';
import { DownloadButtonComponent } from '../../components/download-button/download-button.component';
import { LogTableComponent } from '../../components/log-table/log-table.component';
import { PageComponent } from '../../components/page/page';
import { SelectComponent } from '../../components/select/select.component';
import { ValidationErrorsDialogComponent } from '../../components/validation-errors-dialog/validation-errors-dialog.component';
import { WorkflowLogEntry } from '../../models/workflow-history.model';
import { BusinessEntityService } from '../../services/business-entity/business-entity.service';
import { ProductService } from '../../services/product/product.service';
import { BasePageComponent } from '../base/base.component';

@Component({
  selector: 'app-product-upload',
  imports: [
    PageComponent,
    OuiStackComponent,
    OuiTextComponent,
    CardComponent,
    LogTableComponent,
    OuiButtonComponent,
    OuiFileDropZoneComponent,
    OuiRowComponent,
    DownloadButtonComponent,
    SelectComponent,
    ValidationErrorsDialogComponent,
    OuiBoxComponent,
    OuiCenterComponent,
  ],
  templateUrl: './product-upload.component.html',
})
export class ProductUploadPage extends BasePageComponent {
  selectedFile = signal<File | undefined>(undefined);
  fileDropZone = viewChild<OuiFileDropZoneComponent>('fileDropZone');
  private readonly notification = inject(NotificationService);
  protected readonly entities = inject(BusinessEntityService);
  protected readonly productService = inject(ProductService);

  uploadingProducts = computed(() => {
    const progress = this.productService.productFileUploadProgress();
    return progress !== undefined && progress < 100;
  });

  uploadForm = new FormGroup({
    entityCode: new FormControl(undefined, Validators.required),
  });

  dataImportHistoryDatasource!: OuiDatasource<WorkflowLogEntry>;
  protected readonly validationErrors = signal<string[] | undefined>(undefined);

  constructor() {
    super();

    // Even if the data import history is Initialized, we want to refresh it on each page load.
    this.productService.dataImportHistory.reload();

    effect(() => {
      const data = this.productService.dataImportHistory.value();
      if (!data) return;
      this.dataImportHistoryDatasource = new OuiDatasource(data);
    });
  }

  getProductCatalogFileName = this.productService.productCatalogFileName;

  handleFileAttached(file: File | undefined) {
    if (file) this.selectedFile.set(file);
  }

  uploadFile(file?: File) {
    if (!file) return;
    this.productService
      .uploadProductFile(file, this.uploadForm.controls.entityCode.value || '')
      .subscribe({
        next: () => {
          this.fileDropZone()?.selectedFile.set(undefined);
          this.selectedFile.set(undefined);
        },
        error: (err) => {
          const { errors } = err.error as { errors?: string[] };
          if (errors) {
            this.displayValidationErrors(errors);
          } else {
            const errorMessage =
              err.error || 'Failed to upload the file. Please try again.';
            this.notification.showAlert({
              message: errorMessage,
              variant: 'warn',
              symbolName: 'error',
            });
          }
        },
      });
  }

  // TODO: Refactor to use resource
  /* fileUploadResource = resource({
    request: () => ({
      file: this.selectedFile(),
      entityCode: this.uploadForm.controls.entityCode.value || '',
    }),
    loader: async (params: ResourceLoaderParams<ProductFileImportParams>) => {
      const file = params.request.file;
      if (!file) return;
      const entityCode = params.request.entityCode;
      const response = await firstValueFrom(
        this.productService.uploadProductFile(file, entityCode),
      );
      return response;
    },
  }); */

  displayValidationErrors(errors: string[]) {
    this.validationErrors.set(errors);
  }
}
